/**
 * ClientLoopDashboardPage — Guardian Runtime, the client product surface (Sprint 49).
 *
 * Route: /product/loop (founder-gated for tonight's MVP so the Founder can SEE
 * the buyer-facing product over the same real data). This is the Guardian
 * Runtime *audit* view an operator/auditor reads — NOT the OMEGA founder
 * cockpit. It consumes the existing, unchanged endpoints:
 *   - GET /api/v1/admin/loop/runs      (every run)
 *   - GET /api/v1/admin/loop/summary   (active / approval-queue / degraded)
 *   - GET /api/v1/admin/loop/approvals (T3 dual-control queue)
 *
 * Production client access is per-tenant-scoped — a known follow-up. The page
 * states that honestly in a banner.
 *
 * White, client-grade theme. Loading / empty / error states are all guarded.
 */

import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';

// ── Wire types (mirror crates/gi-api-server/src/{loop_store,routes/loop_control}.rs) ──

interface RunRow {
  run_id: string;
  task: string;
  state: string;
  iteration?: number;
  max_iter?: number;
  quality?: number;
  cost_usd_cents?: number;
  tier?: string;
  started_at?: string;
  updated_at?: string;
}

interface RunsResponse {
  runs?: RunRow[];
}

interface LoopSummary {
  active_loops?: RunRow[];
  active_count?: number;
  t3_approval_queue?: RunRow[];
  degraded?: RunRow[];
  recent_runs?: RunRow[];
  total_runs?: number;
}

interface ApprovalRow {
  approval_id: string;
  run_id: string;
  action_json?: string;
  blast_radius?: string;
  status?: string;
  created_at?: string;
}

interface ApprovalsResponse {
  approvals?: ApprovalRow[];
}

// ── Palette (white, client-grade) ─────────────────────────────────────────────

const ink = '#111827';
const inkMuted = '#4b5563';
const inkSubtle = '#6b7280';
const rim = '#e5e7eb';
const accent = '#1d4ed8';

// ── Tier styling ──────────────────────────────────────────────────────────────

const TIER_STYLE: Record<string, { fg: string; bg: string; border: string; note: string }> = {
  T1: { fg: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', note: 'Autonomous · reversible' },
  T2: { fg: '#b45309', bg: '#fffbeb', border: '#fde68a', note: 'Second-evaluator gate' },
  T3: { fg: '#b91c1c', bg: '#fef2f2', border: '#fecaca', note: 'Pre-execution approval' },
};

function tierStyle(t: string) {
  return TIER_STYLE[t] ?? { fg: '#374151', bg: '#f9fafb', border: rim, note: '' };
}

// ── Status (run state) → human label + tone ───────────────────────────────────

const STATE_TONE: Record<string, string> = {
  done: '#15803d',
  converged: '#15803d',
  executing: '#1d4ed8',
  evaluating: '#1d4ed8',
  planning: '#1d4ed8',
  converging: '#1d4ed8',
  created: '#6b7280',
  awaiting_approval: '#b45309',
  degraded: '#b91c1c',
  aborted: '#b91c1c',
};

function stateTone(s: string): string {
  return STATE_TONE[s] ?? inkSubtle;
}

function usd(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '$0.00';
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function fmtTime(iso: string | undefined): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Tier-gate aggregation (pure) ──────────────────────────────────────────────

interface TierBucket {
  tier: string;
  total: number;
  /** Accrued cost across this tier's runs, USD cents. */
  costCents: number;
  /** Run-state → count, for the status breakdown. */
  byState: Record<string, number>;
}

function bucketByTier(runs: RunRow[]): TierBucket[] {
  const order = ['T1', 'T2', 'T3'];
  const map = new Map<string, TierBucket>();
  for (const t of order) map.set(t, { tier: t, total: 0, costCents: 0, byState: {} });
  for (const r of runs) {
    const tier = r.tier && map.has(r.tier) ? r.tier : 'T1';
    const b = map.get(tier)!;
    b.total += 1;
    b.costCents += r.cost_usd_cents ?? 0;
    b.byState[r.state] = (b.byState[r.state] ?? 0) + 1;
  }
  return order.map((t) => map.get(t)!);
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function ClientLoopDashboardPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const summary = useQuery({
    queryKey: ['client', 'loop', 'summary'],
    queryFn: () => apiFetch<LoopSummary>('/admin/loop/summary'),
    refetchInterval: 15_000,
  });
  const runsQ = useQuery({
    queryKey: ['client', 'loop', 'runs'],
    queryFn: () => apiFetch<RunsResponse>('/admin/loop/runs'),
    refetchInterval: 15_000,
  });
  const approvalsQ = useQuery({
    queryKey: ['client', 'loop', 'approvals'],
    queryFn: () => apiFetch<ApprovalsResponse>('/admin/loop/approvals'),
    refetchInterval: 15_000,
  });

  const runs = runsQ.data?.runs ?? [];
  const buckets = bucketByTier(runs);
  const approvals = approvalsQ.data?.approvals ?? [];
  const totalRuns = summary.data?.total_runs ?? runs.length;

  const anyLoading = summary.isPending || runsQ.isPending || approvalsQ.isPending;
  const anyError = summary.isError || runsQ.isError || approvalsQ.isError;

  return (
    <div
      style={{
        maxWidth: 960,
        margin: '0 auto',
        padding: '28px 24px 48px',
        color: ink,
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
      }}
    >
      {/* Tenant-auth honesty banner */}
      <div
        style={{
          fontSize: 12,
          color: inkSubtle,
          background: '#f9fafb',
          border: `1px solid ${rim}`,
          borderRadius: 8,
          padding: '8px 12px',
          marginBottom: 20,
        }}
      >
        Preview over live data. Production client access is per-tenant scoped — that authentication
        is a known follow-up; this view currently renders under the operator shell.
      </div>

      {/* ── Hero: Guardian Runtime ── */}
      <header style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: accent,
            }}
          >
            Guardian Runtime
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#15803d',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: 999,
              padding: '2px 10px',
            }}
          >
            Tier-gated · Ed25519-signed · replayable
          </span>
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 10px', lineHeight: 1.15 }}>
          Governance you can prove
        </h1>
        <p style={{ fontSize: 16, color: inkMuted, margin: 0, maxWidth: 700, lineHeight: 1.5 }}>
          Observability tells you what your agent did. Guardian Runtime decides what it's allowed to
          do — before it does — and gives you a signed, replayable proof for your auditor.
        </p>
      </header>

      {anyError && (
        <p style={{ fontSize: 13, color: '#b91c1c', marginBottom: 20 }}>
          Some Guardian Runtime data could not be loaded. Retrying automatically.
        </p>
      )}

      {/* ── Tier-Gate audit ── */}
      <section style={{ marginBottom: 36 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <h2 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Tier-gate audit</h2>
          <span style={{ fontSize: 12, color: inkSubtle }}>{totalRuns} decisions on record</span>
        </div>

        {anyLoading && runs.length === 0 && (
          <p style={{ fontSize: 13, color: inkSubtle }}>Loading decision history…</p>
        )}
        {!anyLoading && runs.length === 0 && (
          <div
            style={{
              border: `1px dashed ${rim}`,
              borderRadius: 10,
              padding: '28px 20px',
              textAlign: 'center',
              color: inkSubtle,
              fontSize: 13,
            }}
          >
            No governed decisions yet. As agents run, each decision is gated by tier and recorded
            here — every one signed and replayable.
          </div>
        )}

        {runs.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 14,
            }}
          >
            {buckets.map((b) => {
              const t = tierStyle(b.tier);
              return (
                <div
                  key={b.tier}
                  style={{
                    border: `1px solid ${rim}`,
                    borderRadius: 10,
                    padding: '16px 18px',
                    background: '#ffffff',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 4,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: t.fg,
                        background: t.bg,
                        border: `1px solid ${t.border}`,
                        borderRadius: 999,
                        padding: '2px 10px',
                      }}
                    >
                      {b.tier}
                    </span>
                    <span style={{ fontSize: 22, fontWeight: 800 }}>{b.total}</span>
                  </div>
                  <div style={{ fontSize: 11, color: inkSubtle, marginBottom: 10 }}>
                    {t.note} · {usd(b.costCents)}
                  </div>
                  {Object.keys(b.byState).length === 0 ? (
                    <div style={{ fontSize: 12, color: inkSubtle }}>No decisions at this tier.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {Object.entries(b.byState).map(([state, count]) => (
                        <div
                          key={state}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: 12,
                          }}
                        >
                          <span style={{ color: stateTone(state), fontWeight: 600 }}>
                            {state.replace(/_/g, ' ')}
                          </span>
                          <span style={{ color: inkMuted }}>{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── T3 approvals / parked dual-control ── */}
      <section style={{ marginBottom: 36 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <h2 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>
            Awaiting approval (T3 dual-control)
          </h2>
          <span style={{ fontSize: 12, color: inkSubtle }}>{approvals.length} parked</span>
        </div>

        {approvalsQ.isPending && (
          <p style={{ fontSize: 13, color: inkSubtle }}>Loading approval queue…</p>
        )}
        {!approvalsQ.isPending && approvals.length === 0 && (
          <div
            style={{
              border: `1px dashed ${rim}`,
              borderRadius: 10,
              padding: '24px 20px',
              textAlign: 'center',
              color: inkSubtle,
              fontSize: 13,
            }}
          >
            Nothing parked. High-blast-radius (T3) actions stop here for human sign-off before they
            execute — none are waiting.
          </div>
        )}

        {approvals.length > 0 && (
          <div
            style={{
              border: `1px solid ${rim}`,
              borderRadius: 10,
              overflow: 'hidden',
              background: '#ffffff',
            }}
          >
            {approvals.map((a, i) => (
              <div
                key={a.approval_id}
                style={{
                  padding: '12px 16px',
                  borderTop: i === 0 ? 'none' : `1px solid ${rim}`,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 6,
                    flexWrap: 'wrap',
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#b91c1c',
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: 999,
                      padding: '2px 10px',
                    }}
                  >
                    T3
                  </span>
                  {a.blast_radius && (
                    <span style={{ fontSize: 11, color: inkSubtle }}>blast: {a.blast_radius}</span>
                  )}
                  <span style={{ fontSize: 11, color: inkSubtle, marginLeft: 'auto' }}>
                    {fmtTime(a.created_at)}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: inkMuted,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    wordBreak: 'break-word',
                  }}
                >
                  {a.action_json ?? '(action)'}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Signed, replayable history note ── */}
      <section>
        <div
          style={{
            border: `1px solid ${rim}`,
            borderRadius: 10,
            padding: '16px 18px',
            background: '#f9fafb',
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
            Signed, replayable history
          </div>
          <p style={{ fontSize: 13, color: inkMuted, margin: 0, lineHeight: 1.5 }}>
            Every input, decision, and outcome above is appended to a single Ed25519-signed,
            append-only event history. Hand your auditor the run and they can replay it
            deterministically — same inputs, same decisions, byte-for-byte — and verify each
            signature offline. The authorization is provable, not asserted.
          </p>
        </div>
      </section>
    </div>
  );
}

export default ClientLoopDashboardPage;
